import { useCallback, useRef } from 'react';
import { Box } from '@mui/material';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import {
  Breadcrumb,
  useDataProvider,
  useLocalization,
  useSnackbar,
} from '@ui-framework/index';
import Form from '@product-framework/components/AdvancedForms/Form';
import { observer } from 'mobx-react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ButtonContainer,
  IconLibrary,
  Layout,
  StackablePageButtons,
  PageSection,
  PermissionButton,
} from '@product-framework/index';
import { editSchema } from '../../schemas';
import validationSchema from './validations';
import FormViewer from '../../components/FormViewer';

const FORM_ID = 'MAINTENANCE_SERVICE_JOB_FORM_EDIT_FORM';
const PERMISSION_SP = 'SERVICEJOBFORM';

function ServiceJobFormEdit() {
  const { formatMessage } = useLocalization();
  const formRef = useRef();
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const { id: pageId } = useParams();

  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { put, getApiUrl, get } = useDataProvider(provider);

  const updateApiUrl = `${getApiUrl()}/sxcl/v1/service-job-forms/${pageId}`;

  const preProcessRequestData = data => {
    const {
      createdByUserId,
      id,
      lastModifiedByUserId,
      formUiSchema,
      defaultValues,
      claimUiSchema,
      checkOrder,
      ...other
    } = data;
    return {
      formUiSchema: formUiSchema ? JSON.parse(formUiSchema) : {},
      checkOrder: parseInt(data.checkOrder, 10) || 1,
      defaultValues: defaultValues ? JSON.parse(defaultValues) : {},
      claimUiSchema: claimUiSchema ? JSON.parse(claimUiSchema) : {},
      ...other,
    };
  };

  const preProcessFetchData = data => {
    const {
      createdByUserId,
      id,
      lastModifiedByUserId,
      formUiSchema,
      checkOrder,
      defaultValues,
      claimUiSchema,
      ...other
    } = data;
    return {
      ...other,
      formUiSchema: formUiSchema ? JSON.stringify(formUiSchema, 2, 1) : {},
      checkOrder: parseInt(data.checkOrder, 10) || 1,
      defaultValues: defaultValues ? JSON.stringify(defaultValues, 2, 1) : {},
      claimUiSchema: claimUiSchema ? JSON.stringify(claimUiSchema, 2, 1) : {},
    };
  };

  const fetchDefaultValues = useCallback(async () => {
    const getPageDataApiUrl = `${getApiUrl()}/sxcl/v1/service-job-forms/${pageId}`;
    try {
      const response = await get(getPageDataApiUrl);
      if (response?.ok) {
        return {
          data: preProcessFetchData(response?.data),
          errors: null,
        };
      }
      return {
        data: null,
        errors: response?.errors || null,
      };
    } catch (err) {
      return {
        data: null,
        errors: [{ message: err.message }],
      };
    }
  }, [get, getApiUrl, pageId]);

  const onSubmit = async data => {
    try {
      const requestData = preProcessRequestData(data);
      const response = await put(updateApiUrl, { ...requestData });
      if (response?.ok) {
        snackbar.showMessage(
          formatMessage({
            id: 'JOB_FORM_WAS_SAVED_SUCCESSFULLY',
            defaultMessage: 'Job Form was saved successfully',
          }),
        );
        return {
          nextValues: preProcessFetchData(response?.data),
          errors: null,
        };
      }
      return {
        nextValues: data,
        errors: response?.errors,
      };
    } catch (err) {
      return {
        nextValues: data,
        errors: [{ message: err.message }],
      };
    }
  };

  const breadCrumb = [
    {
      name: formatMessage({
        id: 'DASHBOARD',
        defaultMessage: 'Dashboard',
      }),
      icon: IconLibrary.Dashboard,
      onClick: () => {
        navigate('/dashboard');
      },
    },
    {
      name: formatMessage({
        id: 'JOB_FORMS',
        defaultMessage: 'Job Forms',
      }),
      icon: IconLibrary.ServiceJobForms,
      onClick: () => {
        navigate('/servicejobforms');
      },
    },
    {
      name: formatMessage({
        id: 'EDIT_JOB_FORM',
        defaultMessage: 'Edit Job Form',
      }),
      icon: IconLibrary.ServiceJobForm,
      onClick: undefined,
    },
  ];

  const stackablePageButtons = [
    {
      label: formatMessage({
        id: 'CREATE_JOB_FORM',
        defaultMessage: 'Create Job Form',
      }),
      Icon: IconLibrary.ServiceJobForm,
      variant: 'outlined',
      onClick: () => navigate('/servicejobforms/create'),
      permissionName: 'CAN_CREATE_EDIT_SYSTEM_SETTINGS_PAGES',
    },
    {
      label: formatMessage({
        id: 'LIST_JOB_FORMS',
        defaultMessage: 'List Job Forms',
      }),
      Icon: IconLibrary.ServiceJobForms,
      variant: 'outlined',
      onClick: () => navigate('/servicejobforms'),
    },
  ];

  return (
    <Layout>
      <Grid2 xs={12} container>
        <Grid2 xs={12} md={6}>
          <Breadcrumb items={breadCrumb} />
        </Grid2>
        <Grid2 xs={12} md={6}>
          <StackablePageButtons buttons={stackablePageButtons} />
        </Grid2>
      </Grid2>
      <PageSection
        label={formatMessage({
          id: 'EDIT_JOB_FORM',
          defaultMessage: 'Edit Job Form',
        })}>
        <Box>
          <Form
            formId={FORM_ID}
            ref={formRef}
            permissionEntities={[PERMISSION_SP]}
            onSubmit={onSubmit}
            schema={editSchema}
            validationSchema={validationSchema(formatMessage, getApiUrl, get)}
            fetchDefaultValues={fetchDefaultValues}
            customComponents={{ FormViewer }}>
            <ButtonContainer>
              <PermissionButton
                permissionName="CAN_CREATE_EDIT_SYSTEM_SETTINGS_PAGES"
                variant="contained"
                startIcon={<IconLibrary.Save />}
                color="primary"
                onClick={() => formRef.current?.submit({ formId: FORM_ID })}>
                {formatMessage({
                  id: 'SAVE_JOB_FORM',
                  defaultMessage: 'Save Job Form',
                })}
              </PermissionButton>
              <PermissionButton
                permissionName="CAN_CREATE_EDIT_SYSTEM_SETTINGS_PAGES"
                variant="contained"
                color="primary"
                startIcon={<IconLibrary.Reset />}
                onClick={() => formRef.current?.reset({})}>
                {formatMessage({
                  id: 'RESET',
                  defaultMessage: 'Reset',
                })}
              </PermissionButton>
            </ButtonContainer>
          </Form>
        </Box>
      </PageSection>
    </Layout>
  );
}

export default observer(ServiceJobFormEdit);
